import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Card, CardContent } from '../components/ui/card';
import { Loader2, ExternalLink, Hotel } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HotelsPage = () => {
  const { language } = useLanguage();
  const [hotels, setHotels] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchHotels = async () => {
      try {
        const response = await axios.get(`${API}/hotels`);
        setHotels(response.data);
      } catch (error) {
        console.error('Error fetching hotels:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchHotels();
  }, []);

  const labels = {
    es: { title: 'Hoteles Recomendados', subtitle: 'Los mejores hoteles cerca de la comunidad' },
    en: { title: 'Recommended Hotels', subtitle: 'The best hotels near the community' },
    he: { title: 'מלונות מומלצים', subtitle: 'המלונות הטובים ביותר ליד הקהילה' }
  };
  const l = labels[language] || labels.es;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAF9F6] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#B85042]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF9F6] pt-20 pb-12">
      <section className="relative h-[250px] overflow-hidden mb-8">
        <div className="absolute inset-0 bg-[#0F2922]">
          <div className="absolute inset-0 bg-gradient-to-br from-[#0F2922] via-[#0F2922]/95 to-[#B85042]/20" />
        </div>
        <div className="relative z-10 h-full flex items-center justify-center">
          <div className="text-center px-4">
            <div className="w-16 h-16 rounded-2xl bg-[#B85042] flex items-center justify-center mx-auto mb-4">
              <Hotel className="w-8 h-8 text-white" />
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl font-bold text-white mb-2" data-testid="hotels-title">
              {l.title}
            </h1>
            <p className="font-body text-white/80 text-lg">{l.subtitle}</p>
          </div>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {hotels.sort((a, b) => (a.order || 0) - (b.order || 0)).map((hotel) => (
            <a
              key={hotel.id}
              href={hotel.url}
              target="_blank"
              rel="noopener noreferrer"
              className="block group"
              data-testid={`hotel-${hotel.id}`}
            >
              <Card className="bg-white border-[#E5E3D8] rounded-2xl overflow-hidden hover:shadow-xl hover:-translate-y-1 transition-all duration-300 h-full">
                <div className="h-48 overflow-hidden bg-[#E5E3D8] relative">
                  <img
                    src={hotel.image_url || 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=400'}
                    alt={hotel.name}
                    loading="lazy"
                    onError={(e) => {
                      if (e.currentTarget.dataset.fallback) return;
                      e.currentTarget.dataset.fallback = '1';
                      e.currentTarget.src = 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=600&q=80';
                    }}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    data-testid={`hotel-image-${hotel.id}`}
                  />
                </div>
                <CardContent className="p-5">
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="font-heading text-base font-bold text-[#0F2922] leading-tight">
                      {hotel.name}
                    </h3>
                    <ExternalLink className="w-4 h-4 text-[#5C6C62] flex-shrink-0 mt-1" />
                  </div>
                </CardContent>
              </Card>
            </a>
          ))}
        </div>
      </div>
    </div>
  );
};

export default HotelsPage;
